'use client';

import type { ReactNode } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { Loading } from '@/app/components/icons/Loading';
import type { LiveAvatar } from '@/app/types';
import { cn } from '@/app/utils';
import { useAvatarDisplayImage } from '../_hooks/use-avatar-display-image';
import {
  getAvatarProfilePhotoImage,
  getAvatarRoleSubtitle,
  getBannerAvatarCardImage,
  isUserBannerAvatar,
} from '../utils';

// 渐进式模糊：底部区域分多层叠加 backdrop-blur。
// 关键点：①模糊半径逐层「翻倍」（人眼对模糊呈对数感知，线性取值会出现明显断层）；
// ②每层 mask 从各自起点一路显示到底部，相邻层大面积重叠、模糊逐层累加，
// 越靠下叠加的层越多 → 模糊越强，形成连续平滑的过渡。
const PROGRESSIVE_BLUR_LAYER_COUNT = 8;
const PROGRESSIVE_BLUR_LAYERS = Array.from(
  { length: PROGRESSIVE_BLUR_LAYER_COUNT },
  (_, i) => 0.5 * 2 ** i, // 0.5,1,2,4,8,16,32,64
);

interface BannerAvatarCardProps {
  avatarId: string;
  role: LiveAvatar;
  isOwn: boolean;
  chatLabel: string;
  editLabel: string;
  shareLabel?: string;
  deleteLabel?: string;
  onChat: (avatarId: string, role: LiveAvatar) => void;
  onEdit?: (role: LiveAvatar) => void;
  className?: string;
  /** 该卡片正在 claim（接通中）：展示 loading 遮罩并禁用点击。 */
  isClaiming?: boolean;
  /** 名字与聊天按钮在底部同一行排列（列表页用），默认竖向堆叠。 */
  inlineFooter?: boolean;
  /** 提供后菜单里出现「分享」项，点击回调（阻止冒泡，不触发聊天）。 */
  onShare?: (role: LiveAvatar) => void;
  /** 提供后（自建角色）菜单里出现「删除」项，点击回调（阻止冒泡，不触发聊天）。仅 UGC 角色可删。 */
  onDelete?: (role: LiveAvatar) => void;
}

export function BannerAvatarCard({
  avatarId,
  role,
  isOwn,
  chatLabel,
  editLabel,
  shareLabel,
  deleteLabel,
  onChat,
  onEdit,
  className,
  inlineFooter = false,
  onShare,
  onDelete,
  isClaiming = false,
}: BannerAvatarCardProps) {
  const previewImage = isOwn ? getAvatarProfilePhotoImage(role) : getBannerAvatarCardImage(role);
  const displayImage = useAvatarDisplayImage(previewImage);
  const isUserAvatar = isUserBannerAvatar(role);
  const roleName = role.name ?? '—';
  const roleSubtitle = getAvatarRoleSubtitle(role);

  // 编辑 / 删除收进 more 菜单，方便后续扩展（分享单独放在菜单钮左侧）。二者仅对 UGC 自建角色开放。
  const menuActions: { key: string; label?: string; icon: ReactNode; danger?: boolean; onSelect: () => void }[] = [
    isOwn && onEdit
      ? { key: 'edit', label: editLabel, icon: <BaseLineEdit className="text-lg" />, onSelect: () => onEdit(role) }
      : null,
    isOwn && onDelete
      ? {
          key: 'delete',
          label: deleteLabel,
          icon: <BaseLineDelete className="text-lg" />,
          danger: true,
          onSelect: () => onDelete(role),
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <div
      className={cn(
        'relative aspect-[3/4] w-[158px] shrink-0 overflow-hidden rounded-16 border border-system-white08 sm:w-[calc(50%-2px)]',
        className,
      )}
    >
      {/* 右上角操作区：分享独立成钮；编辑/删除收进 more 菜单，hover / 聚焦展开。以后加操作只需往 menuActions 里加项。 */}
      {onShare || menuActions.length > 0 ? (
        <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
          {onShare ? (
            <button
              type="button"
              aria-label={shareLabel ?? 'share'}
              onClick={(event) => {
                event.stopPropagation();
                onShare(role);
              }}
              className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
            >
              <BaseLineShare className="text-lg" />
            </button>
          ) : null}
          {menuActions.length > 0 ? (
            <div className="group/menu relative">
              <button
                type="button"
                aria-label="more"
                onClick={(event) => event.stopPropagation()}
                className="flex size-7 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 group-focus-within/menu:bg-black/60"
              >
                <BaseLineMore className="text-lg" />
              </button>
              {/* pt-2 兜住触发钮与列表之间的间隙，避免移动光标时 hover 中断 */}
              <div className="pointer-events-none absolute top-full right-0 pt-2 opacity-0 transition-opacity duration-150 group-focus-within/menu:pointer-events-auto group-focus-within/menu:opacity-100 group-hover/menu:pointer-events-auto group-hover/menu:opacity-100">
                <div className="flex min-w-[112px] flex-col overflow-hidden rounded-12 bg-black/60 py-1 backdrop-blur-md">
                  {menuActions.map((action) => (
                    <button
                      key={action.key}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        action.onSelect();
                      }}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                        action.danger ? 'text-[#FF4D4F] hover:bg-[#FF4D4F]/10' : 'text-white hover:bg-white/10',
                      )}
                    >
                      <span className="shrink-0">{action.icon}</span>
                      {action.label ? <span className="truncate">{action.label}</span> : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          'group relative flex h-full w-full items-end justify-center overflow-hidden bg-[rgba(44,44,45,0.70)] backdrop-blur-[40px] duration-300 hover:z-10',
          // 列表页（inlineFooter）：仅图片缩放，标题/按钮不跟随；主页：整卡缩放（保留原效果）
          inlineFooter ? 'transition-colors' : 'transition-transform hover:scale-110 md:hover:scale-100',
        )}
      >
        {displayImage ? (
          <>
            <img
              src={displayImage}
              alt=""
              aria-hidden
              loading="lazy"
              className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
            />
            <img
              src={displayImage}
              alt=""
              loading="lazy"
              className={cn(
                'pointer-events-none absolute inset-0 h-full w-full',
                inlineFooter && 'transition-transform duration-300 group-hover:scale-110 md:group-hover:scale-100',
                isUserAvatar ? 'object-contain' : 'object-cover',
              )}
            />
          </>
        ) : null}
        <div className="pointer-events-none absolute inset-0 bg-[rgba(0,0,0,0.24)] transition-colors duration-300 group-hover:bg-transparent" />
        {inlineFooter
          ? PROGRESSIVE_BLUR_LAYERS.map((blur, index) => {
              // 每层从 start% 处用一小段渐变淡入，之后一路 black 到底部（不淡出）。
              // 这样各层从上到下依次叠加，模糊自然累积，无硬边。
              const start = (index / PROGRESSIVE_BLUR_LAYER_COUNT) * 100;
              const fadeEnd = ((index + 1) / PROGRESSIVE_BLUR_LAYER_COUNT) * 100;
              const mask = `linear-gradient(to bottom, transparent ${start}%, black ${fadeEnd}%, black 100%)`;
              return (
                <div
                  key={blur}
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%]"
                  style={{
                    backdropFilter: `blur(${blur}px)`,
                    WebkitBackdropFilter: `blur(${blur}px)`,
                    maskImage: mask,
                    WebkitMaskImage: mask,
                  }}
                />
              );
            })
          : null}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[30%] bg-[linear-gradient(180deg,rgba(0,0,0,0)0%,rgba(0,0,0,0.7)100%)]" />
        <button
          type="button"
          onClick={() => onChat(avatarId, role)}
          aria-label={chatLabel}
          disabled={isClaiming}
          className="absolute inset-0 z-0 disabled:cursor-not-allowed"
        />
        {isClaiming ? (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Loading className="animate-spin text-2xl text-white" />
          </div>
        ) : null}
        {inlineFooter ? (
          <div className="pointer-events-none relative z-10 flex w-full items-center justify-between px-5 pb-5 sm:flex-col sm:pb-7 sm:text-center">
            <div className="min-w-0 flex-1 text-left sm:w-full sm:flex-none sm:text-center">
              <span className="block max-w-full truncate font-medium text-base text-system-white sm:text-xs">
                {roleName}
              </span>
              {roleSubtitle ? (
                <span className="mt-0.5 block max-w-full truncate text-[13px] text-system-white40 sm:text-[11px]">
                  {roleSubtitle}
                </span>
              ) : null}
            </div>
            {/* 默认零宽（文字占满整行）；hover 时展开腾出按钮位，文字随之收窄 */}
            <span className="inline-flex h-8 max-w-0 shrink-0 items-center justify-center overflow-hidden whitespace-nowrap rounded-full bg-white px-0 font-medium text-black text-sm opacity-0 transition-all duration-300 group-hover:ml-4 group-hover:max-w-[160px] group-hover:px-4 group-hover:opacity-100 sm:mt-2 sm:max-w-none sm:px-4 sm:text-xs sm:opacity-100">
              {chatLabel}
            </span>
          </div>
        ) : (
          <div className="pointer-events-none relative z-10 flex w-full flex-col items-center px-3 pb-3 text-center group-hover:pb-5 sm:pb-5">
            <span className="block w-full max-w-full truncate font-medium text-sm text-system-white sm:text-xs">
              {roleName}
            </span>
            {isOwn && onEdit ? (
              <div className="pointer-events-auto mt-2 flex max-h-0 flex-col items-center gap-2 overflow-hidden opacity-0 transition-all duration-300 group-hover:max-h-24 group-hover:opacity-100 sm:max-h-24 sm:opacity-100">
                <button
                  type="button"
                  onClick={() => onChat(avatarId, role)}
                  className="inline-flex h-8 items-center justify-center rounded-full bg-white px-5 font-medium text-black text-sm sm:text-xs"
                >
                  {chatLabel}
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit(role);
                  }}
                  className="inline-flex h-8 items-center justify-center rounded-full border border-white/40 bg-transparent px-4 font-medium text-sm text-white sm:text-xs"
                >
                  {editLabel}
                </button>
              </div>
            ) : (
              <span className="mt-2 inline-flex h-8 max-h-0 items-center justify-center overflow-hidden rounded-full bg-white px-5 font-medium text-black text-sm opacity-0 transition-all duration-300 group-hover:max-h-8 group-hover:opacity-100 sm:max-h-8 sm:text-xs sm:opacity-100">
                {chatLabel}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
